import {Select} from '@wandb/weave/components/Form/Select';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  type ActionMeta,
  type FormatOptionLabelMeta,
  type InputActionMeta,
} from 'react-select';

import {
  useViewerTeamsProjectsQuery,
  type ViewerTeamsProjectsQuery,
} from '../../../../../generated/graphql';
import {type ProjectOption} from '../types';
import {getProjectOptions} from '../util';
import * as S from './ArtifactPathSelector.styles';

interface ProjectSelectorProps {
  value?: ProjectOption | null;
  onProjectSelect?: (project: ProjectOption | null) => void;
  initialSearchString?: string;
}

export const ProjectSelector = ({
  value,
  onProjectSelect,
  initialSearchString = '',
}: ProjectSelectorProps) => {
  const [projectSearchString, setProjectSearchString] =
    useState(initialSearchString);
  const [inputValue, setInputValue] = useState('');

  const {data: projectData, loading: projectsLoading} =
    useViewerTeamsProjectsQuery({
      variables: {
        pattern: projectSearchString,
      },
    });

  const projectOptions = getProjectOptions(
    projectData ?? ({} as ViewerTeamsProjectsQuery)
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchString: string) => {
        setProjectSearchString(searchString);
      }, 300),
    []
  );

  const onInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change') {
        setInputValue(newValue);
        debouncedSearch(newValue);
      }
    },
    [debouncedSearch]
  );

  // prevent dropdown from closing during search by waiting for initial load
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  useEffect(() => {
    if (!projectsLoading) {
      setInitialLoadComplete(true);
    }
  }, [projectsLoading]);

  // keep input in sync with external selection state
  useEffect(() => {
    if (!value) {
      setInputValue('');
    }
  }, [value]);

  const handleChange = useCallback(
    (option: ProjectOption | null, actionMeta: ActionMeta<ProjectOption>) => {
      if (actionMeta.action === 'clear') {
        debouncedSearch.cancel();
        setProjectSearchString('');
        setInputValue('');
      }

      // make sure input value is cleared when an option is selected
      // otherwise it'll show the search text instead of the project name
      if (actionMeta.action === 'select-option') {
        setInputValue('');
      }

      if (onProjectSelect) {
        onProjectSelect(option);
      }
    },
    [onProjectSelect, debouncedSearch]
  );

  if (!initialLoadComplete) {
    return <Select isDisabled placeholder="Loading projects..." />;
  }

  return (
    <Select
      value={value}
      inputValue={inputValue}
      isClearable={true}
      options={projectOptions}
      placeholder="Select project"
      formatOptionLabel={renderProjectOptionLabel}
      onInputChange={onInputChange}
      onChange={handleChange}
      isSearchable
    />
  );
};

const renderProjectOptionLabel = (
  option: ProjectOption,
  context: FormatOptionLabelMeta<ProjectOption>
) => {
  const {entityName, projectName} = option;

  if (context.context === 'value') {
    return (
      <S.OptionLabel>
        <S.OptionaLabelText>{projectName}</S.OptionaLabelText>
      </S.OptionLabel>
    );
  }

  return (
    <S.OptionLabel>
      <S.OptionaLabelText>{projectName}</S.OptionaLabelText>
      <S.OptionLabelSubtitleText>{entityName}</S.OptionLabelSubtitleText>
    </S.OptionLabel>
  );
};
